import React, { useEffect, useState, useCallback } from 'react';
import apiService, { AdminUser } from '../../services/apiService';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

type Role = 'admin' | 'bearbeiter';

interface FormState {
  id?: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  language: string;
  role: Role;
  isActive: boolean;
  password: string;
}

const EMPTY_FORM: FormState = {
  username: '',
  email: '',
  firstName: '',
  lastName: '',
  language: 'de',
  role: 'bearbeiter',
  isActive: true,
  password: '',
};

const UserManagement: React.FC = () => {
  const { addToast } = useToast();
  const { user: me } = useAuth();

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [pwUserId, setPwUserId] = useState<number | null>(null);
  const [pwValue, setPwValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiService.get<AdminUser[]>('/users');
      if (resp.data?.success) setUsers(resp.data.data || []);
      else addToast(resp.data?.message || 'Konnte Benutzer nicht laden.', 'error');
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Konnte Benutzer nicht laden.', 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setShowModal(true);
  };
  const openEdit = (u: AdminUser) => {
    setForm({
      id: u.id,
      username: u.username,
      email: u.email,
      firstName: u.first_name,
      lastName: u.last_name,
      language: u.language || 'de',
      role: u.role,
      isActive: u.is_active,
      password: '',
    });
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
    setForm(EMPTY_FORM);
  };

  const handleSave = async () => {
    if (!form.username || !form.email || !form.firstName || !form.lastName) {
      addToast('Bitte alle Pflichtfelder ausfüllen.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        const resp = await apiService.put<AdminUser>(`/users/${form.id}`, {
          username: form.username,
          email: form.email,
          firstName: form.firstName,
          lastName: form.lastName,
          language: form.language,
          role: form.role,
          isActive: form.isActive,
        });
        if (resp.data?.success) {
          addToast('Benutzer aktualisiert.', 'success');
          closeModal();
          await load();
        } else {
          addToast(resp.data?.message || 'Speichern fehlgeschlagen.', 'error');
        }
      } else {
        if (!form.password || form.password.length < 8) {
          addToast('Passwort muss mindestens 8 Zeichen haben.', 'error');
          setSaving(false);
          return;
        }
        const resp = await apiService.post<AdminUser>('/users', {
          username: form.username,
          email: form.email,
          password: form.password,
          firstName: form.firstName,
          lastName: form.lastName,
          language: form.language,
          role: form.role,
          isActive: form.isActive,
        });
        if (resp.data?.success) {
          addToast('Benutzer angelegt.', 'success');
          closeModal();
          await load();
        } else {
          addToast(resp.data?.message || 'Anlegen fehlgeschlagen.', 'error');
        }
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Speichern fehlgeschlagen.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (u: AdminUser) => {
    if (u.id === me?.id) {
      addToast('Du kannst dich nicht selbst löschen.', 'error');
      return;
    }
    if (!window.confirm(`Benutzer "${u.username}" wirklich löschen?`)) return;
    try {
      const resp = await apiService.delete<any>(`/users/${u.id}`);
      if (resp.data?.success) {
        addToast('Benutzer gelöscht.', 'success');
        await load();
      } else {
        addToast(resp.data?.message || 'Löschen fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Löschen fehlgeschlagen.', 'error');
    }
  };

  const handlePasswordReset = async () => {
    if (pwUserId == null) return;
    if (!pwValue || pwValue.length < 8) {
      addToast('Passwort muss mindestens 8 Zeichen haben.', 'error');
      return;
    }
    try {
      const resp = await apiService.patch<any>(`/users/${pwUserId}/password`, { password: pwValue });
      if (resp.data?.success) {
        addToast('Passwort gesetzt.', 'success');
        setPwUserId(null);
        setPwValue('');
      } else {
        addToast(resp.data?.message || 'Passwort-Reset fehlgeschlagen.', 'error');
      }
    } catch (e: any) {
      addToast(e?.response?.data?.message || 'Passwort-Reset fehlgeschlagen.', 'error');
    }
  };

  return (
    <section className="bg-white rounded-lg shadow p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">👥 Benutzerverwaltung</h2>
          <p className="text-sm text-gray-600 mt-1">
            <strong>Admin</strong> kann Benutzer verwalten. <strong>Bearbeiter</strong> kann nur normal arbeiten.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="px-4 py-2 bg-navy-admin text-white rounded font-semibold hover:bg-opacity-90"
        >
          + Neuer Benutzer
        </button>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm">Lade Benutzer…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Benutzer</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Email</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Rolle</th>
                <th className="px-3 py-2 text-left font-semibold text-gray-600">Status</th>
                <th className="px-3 py-2 text-right font-semibold text-gray-600">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(u => (
                <tr key={u.id} className={u.id === me?.id ? 'bg-mint/5' : ''}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{u.first_name} {u.last_name}</div>
                    <div className="text-xs text-gray-500">@{u.username}{u.id === me?.id && <span className="ml-2 text-mint font-semibold">(du)</span>}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-700">{u.email}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                      u.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {u.role === 'admin' ? 'Admin' : 'Bearbeiter'}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${
                      u.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'
                    }`}>
                      {u.is_active ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right space-x-2 whitespace-nowrap">
                    <button
                      onClick={() => openEdit(u)}
                      className="text-light-blue hover:underline text-sm font-medium"
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => { setPwUserId(u.id); setPwValue(''); }}
                      className="text-gray-700 hover:underline text-sm font-medium"
                    >
                      Passwort
                    </button>
                    <button
                      onClick={() => handleDelete(u)}
                      disabled={u.id === me?.id}
                      className="text-red-600 hover:underline text-sm font-medium disabled:opacity-30 disabled:no-underline disabled:cursor-not-allowed"
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-500">Keine Benutzer.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / Edit modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeModal}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900">
              {form.id ? 'Benutzer bearbeiten' : 'Neuen Benutzer anlegen'}
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Vorname *</label>
                <input className="w-full p-2 border border-gray-300 rounded text-sm" value={form.firstName}
                  onChange={e => setForm({ ...form, firstName: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Nachname *</label>
                <input className="w-full p-2 border border-gray-300 rounded text-sm" value={form.lastName}
                  onChange={e => setForm({ ...form, lastName: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Benutzername *</label>
                <input className="w-full p-2 border border-gray-300 rounded text-sm" value={form.username}
                  onChange={e => setForm({ ...form, username: e.target.value })} autoComplete="off" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Email *</label>
                <input type="email" className="w-full p-2 border border-gray-300 rounded text-sm" value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })} autoComplete="off" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Rolle</label>
                <select
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                  value={form.role}
                  onChange={e => setForm({ ...form, role: e.target.value as Role })}
                  disabled={form.id === me?.id}
                  title={form.id === me?.id ? 'Du kannst deine eigene Rolle nicht ändern' : ''}
                >
                  <option value="bearbeiter">Bearbeiter</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Sprache</label>
                <select className="w-full p-2 border border-gray-300 rounded text-sm" value={form.language}
                  onChange={e => setForm({ ...form, language: e.target.value })}>
                  <option value="de">Deutsch</option>
                  <option value="es">Español</option>
                  <option value="en">English</option>
                  <option value="ru">Русский</option>
                </select>
              </div>
              {!form.id && (
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Passwort * (min. 8 Zeichen)</label>
                  <input type="password" className="w-full p-2 border border-gray-300 rounded text-sm"
                    value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                    autoComplete="new-password" />
                </div>
              )}
              <div className="sm:col-span-2 flex items-center gap-2">
                <input id="isActive" type="checkbox" className="h-4 w-4"
                  checked={form.isActive}
                  onChange={e => setForm({ ...form, isActive: e.target.checked })}
                  disabled={form.id === me?.id}
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">Aktiv (Login erlaubt)</label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={closeModal} className="px-4 py-2 text-gray-600 hover:text-gray-900 text-sm">Abbrechen</button>
              <button onClick={handleSave} disabled={saving}
                className="px-5 py-2 bg-navy-admin text-white rounded font-semibold disabled:opacity-50">
                {saving ? 'Speichere…' : (form.id ? 'Speichern' : 'Anlegen')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password reset modal */}
      {pwUserId != null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPwUserId(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-900">Passwort setzen</h3>
            <p className="text-sm text-gray-600">
              Setze ein neues Passwort für {users.find(u => u.id === pwUserId)?.username}.
              Der Benutzer wird beim nächsten Login das neue Passwort verwenden.
            </p>
            <input type="password" className="w-full p-2 border border-gray-300 rounded text-sm"
              placeholder="Neues Passwort (min. 8 Zeichen)" autoComplete="new-password"
              value={pwValue} onChange={e => setPwValue(e.target.value)} />
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => setPwUserId(null)} className="px-4 py-2 text-gray-600 hover:text-gray-900 text-sm">Abbrechen</button>
              <button onClick={handlePasswordReset}
                className="px-5 py-2 bg-navy-admin text-white rounded font-semibold">
                Setzen
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default UserManagement;
